import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import WorkOutlineRoundedIcon from "@material-ui/icons/WorkOutlineRounded";
import StarsRoundedIcon from "@material-ui/icons/StarsRounded";
import SchoolRoundedIcon from "@material-ui/icons/SchoolRounded";
import CardMembershipRoundedIcon from "@material-ui/icons/CardMembershipRounded";
import AssignmentIndRoundedIcon from "@material-ui/icons/AssignmentIndRounded";
import TranslateRoundedIcon from "@material-ui/icons/TranslateRounded";
import MonetizationOnRoundedIcon from "@material-ui/icons/MonetizationOnRounded";
import BusinessCenterRoundedIcon from "@material-ui/icons/BusinessCenterRounded";
import LocationOnRoundedIcon from "@material-ui/icons/LocationOnRounded";
import axios from "axios";
import React, { Fragment, useEffect, useState } from "react";
import {
  TranformAdditionalInformationData,
  getInitials,
} from "../../config/candidatesConfigData";
import AxiosErrorHandling from "../AxiosErrorHandling/AxiosErrorHandling";
import {
  AI_EXPERIENCES,
  AI_SKILLS,
  AI_ACADEMIC_DGREE,
  AI_CERTIFICATES,
  AI_LICENSES,
  AI_LANGUAGES,
  AI_SALARY_EXPECTATIONS,
} from "../../config/constants";
import {
  translateDegreeAndType,
  translateLanguageLevel,
  translateSkillLevel,
} from "../../config/jobsConfigData";

// ----- presentation helpers --------------------------------------------------

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 540,
    [theme.breakpoints.down("sm")]: { width: "100vw" },
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: theme.palette.background.paper,
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  closeRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(0.5),
  },
  avatar: {
    width: 56,
    height: 56,
    marginLeft: theme.spacing(1.5),
    fontSize: "1.25rem",
  },
  identityStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.8125rem",
  },
  identityChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  body: {
    padding: theme.spacing(2, 2.5),
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitleIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
  },
  sectionTitle: {
    letterSpacing: 1,
    color: theme.palette.text.secondary,
  },
  chipGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  levelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  salaryValue: {
    fontWeight: 500,
    fontSize: "1rem",
  },
  salaryMissing: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
}));

// Salary formatter — handles every shape the transform can emit:
//   missing / both zero          → null  (caller hides the section)
//   single value (min === max)   → "₪10,000 / חודשי"
//   range                        → "₪10,000 – ₪12,000 / חודשי"
// Measure suffix: 'hour' → "שעתי", anything else → "חודשי" (per cd_candidates_additional_information).
const formatSalary = (min, max, measure) => {
  const lo = Number(min) || 0;
  const hi = Number(max) || 0;
  if (lo <= 0 && hi <= 0) return null;
  const suffix = measure === "hour" ? " / שעתי" : " / חודשי";
  const fmt = (n) => `₪${Number(n).toLocaleString("he-IL")}`;
  if (lo === hi || !hi) return `${fmt(lo || hi)}${suffix}`;
  if (!lo) return `${fmt(hi)}${suffix}`;
  return `${fmt(lo)} – ${fmt(hi)}${suffix}`;
};

const chipColorForLevel = (raw) => {
  // raw values seen in DB: 'expert','advanced','medium','basic','low','high','veryGood'
  const v = String(raw || "").toLowerCase();
  if (["expert", "advanced", "high", "verygood"].includes(v)) return "primary";
  if (["medium", "intermediate", "good"].includes(v)) return "default";
  return "default";
};

const Section = ({ icon, title, children, empty }) => {
  const classes = useStyles();
  if (empty) return null;
  return (
    <Box my={2.5}>
      <div className={classes.sectionTitleRow}>
        <span className={classes.sectionTitleIcon}>{icon}</span>
        <Typography
          variant="overline"
          className={classes.sectionTitle}
        >
          {title}
        </Typography>
      </div>
      {children}
    </Box>
  );
};

const IdentityChip = ({ icon, value }) => {
  const classes = useStyles();
  if (value === null || value === undefined || value === "") return null;
  return (
    <span className={classes.identityChip}>
      {icon}
      {value}
    </span>
  );
};

// ----- component -------------------------------------------------------------

const CandidateQuickView = ({ open, onClose, data }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setProfileData(null);

    axios
      .get("/wp-json/api/v2/candidateAI/" + data.id)
      .then((res) => {
        if (cancelled) return;
        const transformed = TranformAdditionalInformationData(res.data);
        const localProfileData = [];

        transformed.forEach((aiData) => {
          const key = Object.keys(aiData)[0];
          const values = Object.values(aiData)[0];
          switch (key) {
            case AI_EXPERIENCES: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (exp) => {
                  let sId = 0;
                  let sName = "";
                  let years = 0;
                  let isManagementRole = false;
                  let tags = [];
                  exp.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "experienceName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "yearsExperience":
                        years = parseInt(singleExp.ai_value);
                        break;
                      case "isManagementRole":
                        isManagementRole = parseInt(singleExp.ai_value) === 1;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    experienceId: sId,
                    experienceName: sName,
                    yearsExperience: years,
                    isManagementRole,
                  };
                }
              );
              localProfileData.push({
                AI_EXPERIENCES: localAIObject.filter(
                  (aiItem) => aiItem.experienceId !== "-1"
                ),
              });
              break;
            }
            case AI_SKILLS: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sName = "";
                  let years = 0;
                  let level = false;
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "skillName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "yearsSkill":
                        years = parseInt(singleExp.ai_value);
                        break;
                      case "knowledgeLevel":
                        level = singleExp.ai_value;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    skillId: sId,
                    skillName: sName,
                    yearsSkill: years,
                    knowledgeLevel: level,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_SKILLS: localAIObject.filter(
                  (aiItem) => aiItem.skillId !== "-1"
                ),
              });
              break;
            }
            case AI_ACADEMIC_DGREE: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sName = "";
                  let type = "";
                  let dOn = "";
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "degreeName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "degreeType":
                        type = singleExp.ai_value;
                        break;
                      case "degreeOn":
                        dOn = singleExp.ai_value;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    degreeId: sId,
                    degreeName: sName,
                    degreeType: type,
                    degreeOn: dOn,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_ACADEMIC_DGREE: localAIObject.filter(
                  (aiItem) => aiItem.degreeId !== "-1"
                ),
              });
              break;
            }
            case AI_CERTIFICATES: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sName = "";
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "certificateName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    certificateId: sId,
                    certificateName: sName,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_CERTIFICATES: localAIObject.filter(
                  (aiItem) => aiItem.certificateId !== "-1"
                ),
              });
              break;
            }
            case AI_LICENSES: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sName = "";
                  let years = 0;
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "licenseName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "licenseYears":
                        years = parseInt(singleExp.ai_value);
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    licenseId: sId,
                    licenseName: sName,
                    licenseYears: years,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_LICENSES: localAIObject.filter(
                  (aiItem) => aiItem.licenseId !== "-1"
                ),
              });
              break;
            }
            case AI_LANGUAGES: {
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sName = "";
                  let level = false;
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "languageName":
                        sName = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "languageLevel":
                        level = singleExp.ai_value;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    languageId: sId,
                    languageName: sName,
                    languageLevel: level,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_LANGUAGES: localAIObject.filter(
                  (aiItem) => aiItem.languageId !== "-1"
                ),
              });
              break;
            }
            case AI_SALARY_EXPECTATIONS: {
              // Keep min/max/measure as separate fields so the renderer can
              // distinguish "single value" / "range" / "missing" cleanly,
              // rather than collapsing to a scalar-or-array shape that the
              // old code indexed unconditionally as [0] / [1] → undefined.
              const localAIObject = Object.values(values.typeBySubType).map(
                (aiItem) => {
                  let sId = 0;
                  let sRole = "";
                  let sMeasure = "";
                  let min = 0;
                  let max = 0;
                  let tags = [];
                  aiItem.forEach((singleExp) => {
                    switch (singleExp.ai_key) {
                      case "seForRole":
                        sRole = singleExp.ai_value;
                        sId = singleExp.ai_sub_type_id;
                        break;
                      case "seSalaryMinimum":
                        min = parseFloat(singleExp.ai_value);
                        break;
                      case "seSalaryMaximum":
                        max = parseFloat(singleExp.ai_value);
                        break;
                      case "seMeasure":
                        sMeasure = singleExp.ai_value;
                        break;
                      case "relatedFQTag":
                        sId = singleExp.ai_sub_type_id;
                        tags = [...tags, parseInt(singleExp.ai_value)];
                        break;
                      default:
                        break;
                    }
                  });
                  return {
                    seId: sId,
                    seForRole: sRole,
                    seMin: min,
                    seMax: max,
                    seMeasure: sMeasure,
                    tags,
                  };
                }
              );
              localProfileData.push({
                AI_SALARY_EXPECTATIONS: localAIObject.filter(
                  (aiItem) => aiItem.seId !== "-1"
                ),
              });
              break;
            }
            default:
              break;
          }
        });

        setProfileData(localProfileData);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response || err);
        setIsLoading(false);
        setProfileData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [data.id]);

  // -------- render sections ----------

  const sectionByKey = (profile, k) => {
    if (!profile) return [];
    const found = profile.find((item) => Object.keys(item)[0] === k);
    return found ? Object.values(found)[0] : [];
  };

  const renderSections = () => {
    if (!profileData) return null;

    const experiences = sectionByKey(profileData, "AI_EXPERIENCES");
    const skills = sectionByKey(profileData, "AI_SKILLS");
    const degrees = sectionByKey(profileData, "AI_ACADEMIC_DGREE");
    const certificates = sectionByKey(profileData, "AI_CERTIFICATES");
    const licenses = sectionByKey(profileData, "AI_LICENSES");
    const languages = sectionByKey(profileData, "AI_LANGUAGES");
    const salaries = sectionByKey(profileData, "AI_SALARY_EXPECTATIONS");

    return (
      <Fragment>
        {/* Salary first — it's a high-signal data point recruiters look for fast. */}
        <Section
          icon={<MonetizationOnRoundedIcon />}
          title="ציפיות שכר"
          empty={salaries.length === 0}
        >
          {salaries.map((s) => {
            const text = formatSalary(s.seMin, s.seMax, s.seMeasure);
            return (
              <Box
                key={s.seId}
                display="flex"
                alignItems="center"
                gridGap={8}
                py={0.5}
              >
                <Typography
                  variant="body1"
                  className={
                    text ? classes.salaryValue : classes.salaryMissing
                  }
                >
                  {text || "לא צוין"}
                </Typography>
                {s.seForRole && (
                  <Chip size="small" label={s.seForRole} variant="outlined" />
                )}
              </Box>
            );
          })}
        </Section>

        <Section
          icon={<WorkOutlineRoundedIcon />}
          title="ניסיון תעסוקתי"
          empty={experiences.length === 0}
        >
          <div className={classes.chipGroup}>
            {experiences.map((exp) => (
              <Tooltip
                key={`${exp.experienceId}-${exp.experienceName}`}
                title={
                  exp.isManagementRole
                    ? `${exp.yearsExperience} שנים · תפקיד ניהולי`
                    : `${exp.yearsExperience} שנים`
                }
              >
                <Chip
                  size="small"
                  color={exp.isManagementRole ? "primary" : "default"}
                  label={`${exp.experienceName} · ${exp.yearsExperience}+`}
                />
              </Tooltip>
            ))}
          </div>
        </Section>

        <Section
          icon={<StarsRoundedIcon />}
          title="כישורים"
          empty={skills.length === 0}
        >
          <Box>
            {skills.map((skl) => (
              <div
                key={`${skl.skillId}-${skl.skillName}`}
                className={classes.levelRow}
              >
                <Typography variant="body2">{skl.skillName}</Typography>
                <Chip
                  size="small"
                  color={chipColorForLevel(skl.knowledgeLevel)}
                  label={translateSkillLevel(skl.knowledgeLevel)}
                />
              </div>
            ))}
          </Box>
        </Section>

        <Section
          icon={<SchoolRoundedIcon />}
          title="השכלה"
          empty={degrees.length === 0}
        >
          {degrees.map((degree) => (
            <Box
              key={degree.degreeId}
              display="flex"
              alignItems="center"
              gridGap={8}
              py={0.5}
            >
              <Typography variant="body2">
                {translateDegreeAndType(degree.degreeName)}
                {degree.degreeOn ? ` · ${degree.degreeOn}` : ""}
              </Typography>
              {degree.degreeType && (
                <Chip size="small" label={degree.degreeType} />
              )}
            </Box>
          ))}
        </Section>

        <Section
          icon={<TranslateRoundedIcon />}
          title="שפות"
          empty={languages.length === 0}
        >
          <Box>
            {languages.map((lang) => (
              <div
                key={`${lang.languageId}-${lang.languageName}`}
                className={classes.levelRow}
              >
                <Typography variant="body2">{lang.languageName}</Typography>
                <Chip
                  size="small"
                  color={chipColorForLevel(lang.languageLevel)}
                  label={translateLanguageLevel(lang.languageLevel)}
                />
              </div>
            ))}
          </Box>
        </Section>

        <Section
          icon={<CardMembershipRoundedIcon />}
          title="תעודות"
          empty={certificates.length === 0}
        >
          <div className={classes.chipGroup}>
            {certificates.map((cert) => (
              <Chip
                key={`${cert.certificateId}-${cert.certificateName}`}
                size="small"
                label={cert.certificateName}
              />
            ))}
          </div>
        </Section>

        <Section
          icon={<AssignmentIndRoundedIcon />}
          title="רישיונות"
          empty={licenses.length === 0}
        >
          <div className={classes.chipGroup}>
            {licenses.map((license) => (
              <Tooltip
                key={`${license.licenseId}-${license.licenseName}`}
                title={
                  license.licenseYears > 0
                    ? `${license.licenseYears} שנים`
                    : ""
                }
              >
                <Chip
                  size="small"
                  label={
                    license.licenseYears > 0
                      ? `${license.licenseName} · ${license.licenseYears}+`
                      : license.licenseName
                  }
                />
              </Tooltip>
            ))}
          </div>
        </Section>
      </Fragment>
    );
  };

  const renderSkeleton = () => (
    <Box className={classes.body}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} my={2.5}>
          <Skeleton variant="text" width={120} height={20} />
          <Box mt={1} display="flex" gridGap={8}>
            <Skeleton variant="rect" width={90} height={28} />
            <Skeleton variant="rect" width={70} height={28} />
            <Skeleton variant="rect" width={110} height={28} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  const identity = (
    <div className={classes.identityStrip}>
      <IdentityChip value={`ID ${data.id}`} />
      {data.acf.externalNumber && (
        <IdentityChip value={`Ext ${data.acf.externalNumber}`} />
      )}
      {data.acf.yearOfBirth && (
        <IdentityChip value={`גיל ${new Date().getFullYear() - data.acf.yearOfBirth}`} />
      )}
      {data.acf.city && (
        <IdentityChip
          icon={<LocationOnRoundedIcon style={{ fontSize: 14 }} />}
          value={data.acf.city}
        />
      )}
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawer }}
    >
      <div className={classes.header}>
        <div className={classes.closeRow}>
          <IconButton onClick={onClose} size="small" aria-label="סגור">
            <CloseRoundedIcon />
          </IconButton>
        </div>
        <Box display="flex" alignItems="center">
          <Avatar
            className={classes.avatar}
            alt={data.name}
            src={data && data.profile_image ? data.profile_image : ""}
          >
            {getInitials(data.name)}
          </Avatar>
          <Box>
            <Typography variant="h6" style={{ lineHeight: 1.2 }}>
              {data.name}
            </Typography>
            {identity}
          </Box>
        </Box>

        {data.professions && data.professions.length > 0 && (
          <Box mt={1.5} display="flex" alignItems="center" gridGap={8}>
            <BusinessCenterRoundedIcon
              style={{ fontSize: 16, color: theme.palette.text.secondary }}
            />
            <Box display="flex" flexWrap="wrap" gridGap={6}>
              {data.professions.map((p) => (
                <Chip
                  key={p.id}
                  size="small"
                  variant="outlined"
                  label={p.name}
                />
              ))}
            </Box>
          </Box>
        )}
      </div>

      {isLoading ? (
        renderSkeleton()
      ) : (
        <Box className={classes.body}>
          {renderSections()}
          {!isLoading &&
            profileData &&
            profileData.every((item) => Object.values(item)[0].length === 0) && (
              <Box my={4} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  אין נתוני שאלון לפרופיל המועמד.
                </Typography>
              </Box>
            )}
        </Box>
      )}

      {error && (
        <Box className={classes.body}>
          <AxiosErrorHandling
            title="אירעה שגיאה בעת ניסיון קריאת מידע של מועמד"
            error={error}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default CandidateQuickView;
